import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/retos/$id")({
  head: () => ({
    meta: [
      { title: "Detalle del reto — Watts" },
      { name: "description", content: "Progreso detallado del reto de Watts." },
      { property: "og:title", content: "Detalle del reto — Watts" },
      { property: "og:description", content: "Progreso detallado del reto de Watts." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: RetoDetalle,
});

interface Challenge {
  id: string;
  name: string;
  description: string | null;
  emoji: string | null;
}

interface ChallengePort {
  id: string;
  challenge_id: string;
  port_id: string;
  sort_order: number | null;
}

interface Port {
  id: string;
  name: string;
  comarca: string | null;
  altitude_m: number | null;
  category: string | null;
}

interface Completion {
  port_id: string;
  status: "approved" | "in_review" | "rejected";
}

function RetoDetalle() {
  const { id } = Route.useParams();
  const { user, loading: authLoading } = useAuth();

  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [ports, setPorts] = useState<Port[]>([]);
  const [completions, setCompletions] = useState<Completion[]>([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!user) return;

    const load = async () => {
      setBusy(true);
      try {
        const [{ data: ch }, { data: cp }, { data: pc }] = await Promise.all([
          supabase
            .from("challenges")
            .select("id, name, description, emoji")
            .eq("id", id)
            .single(),
          supabase
            .from("challenge_ports")
            .select("id, challenge_id, port_id, sort_order")
            .eq("challenge_id", id)
            .order("sort_order"),
          supabase
            .from("port_completions")
            .select("port_id, status")
            .eq("user_id", user.id),
        ]);

        setChallenge(ch as Challenge | null);
        const portIds = (cp as ChallengePort[] ?? []).map((p) => p.port_id);

        if (portIds.length > 0) {
          const { data: portsData } = await supabase
            .from("ports")
            .select("id, name, comarca, altitude_m, category")
            .in("id", portIds);

          const orderedPorts = (cp as ChallengePort[] ?? [])
            .map((cpItem) => ({
              ...cpItem,
              port: (portsData as Port[] ?? []).find((p) => p.id === cpItem.port_id),
            }))
            .filter((item) => item.port)
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
            .map((item) => item.port!);

          setPorts(orderedPorts);
        } else {
          setPorts([]);
        }

        setCompletions((pc as Completion[] ?? []).map((c) => ({
          port_id: c.port_id,
          status: ["approved", "in_review", "rejected"].includes(c.status)
            ? (c.status as Completion["status"])
            : "rejected",
        })));
      } finally {
        setBusy(false);
      }
    };

    load();
  }, [id, user?.id]);

  const total = ports.length;
  const done = completions.filter(
    (c) => c.status === "approved" && ports.some((p) => p.id === c.port_id)
  ).length;
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;

  const statusFor = (portId: string): Completion["status"] => {
    const completion = completions.find((c) => c.port_id === portId);
    if (!completion) return "rejected";
    return completion.status;
  };

  return (
    <div className="h-screen overflow-y-auto bg-background fade-in pb-24">
      <header className="flex items-center gap-3 px-5 pt-[max(env(safe-area-inset-top),1rem)] pb-4">
        <Link
          to="/retos"
          className="press-scale flex h-10 w-10 items-center justify-center rounded-full bg-surface text-foreground"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="font-display text-2xl text-foreground">
          {challenge ? `${challenge.emoji ?? "🏔️"} ${challenge.name}` : "Detalle del reto"}
        </h1>
      </header>

      <main className="px-4 pb-8">
        <div className="rounded-3xl border border-border bg-surface p-6">
          <div className="flex items-end justify-between gap-2">
            <span className="font-display text-6xl text-primary">{percent}%</span>
            <span className="mb-1.5 text-sm text-muted-foreground">
              {done} de {total} coronados
            </span>
          </div>
          <div className="mt-4 h-5 overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full bg-primary transition-all duration-500"
              style={{ width: `${percent}%` }}
            />
          </div>
          {challenge?.description && (
            <p className="mt-4 text-sm text-muted-foreground">{challenge.description}</p>
          )}
        </div>

        <section className="mt-6">
          <h2 className="px-1 font-display text-xl text-foreground">Puertos del reto</h2>

          {busy && (
            <div className="mt-4 space-y-3">
              <div className="h-20 animate-pulse rounded-2xl bg-surface" />
              <div className="h-20 animate-pulse rounded-2xl bg-surface" />
              <div className="h-20 animate-pulse rounded-2xl bg-surface" />
            </div>
          )}

          {!busy && ports.length === 0 && (
            <p className="mt-4 px-1 text-sm text-muted-foreground">Este reto no tiene puertos asignados.</p>
          )}

          {!busy && ports.length > 0 && (
            <ul className="mt-4 space-y-3">
              {ports.map((port) => {
                const status = statusFor(port.id);
                const isApproved = status === "approved";
                const isInReview = status === "in_review";
                const isPending = status === "rejected";

                return (
                  <li key={port.id}>
                    <Link
                      to="/puerto/$id"
                      params={{ id: port.id }}
                      className="press-scale flex items-center gap-4 rounded-2xl border border-border bg-surface p-4"
                    >
                      <span
                        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 ${
                          isApproved
                            ? "border-primary bg-primary text-primary-foreground"
                            : isInReview
                            ? "border-[--gravel] bg-[--gravel] text-white"
                            : "border-white/20 bg-transparent"
                        }`}
                      >
                        {isApproved && <Check className="h-6 w-6" strokeWidth={3} />}
                      </span>

                      <div className="min-w-0 flex-1">
                        <h3 className="font-display text-lg text-foreground">{port.name}</h3>
                        <p className="mt-0.5 text-sm text-muted-foreground">
                          {[port.comarca, port.altitude_m ? `${port.altitude_m} m` : null, port.category]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      </div>

                      <span
                        className={`shrink-0 text-sm font-semibold ${
                          isApproved ? "text-primary" : isInReview ? "text-[--gravel]" : "text-muted-foreground"
                        }`}
                      >
                        {isApproved ? "Coronado" : isInReview ? "En revisión" : "Pendiente"}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
